import { gql, makeVar, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { EReactionType } from '@/graphql-types/globalTypes';
import { post_liked } from '@/analytics';

import {
  useAutoLikePost_createPostReaction,
  useAutoLikePost_createPostReactionVariables,
} from './graphql-types/useAutoLikePost_createPostReaction';

const LIKE_POST = gql`
  mutation useAutoLikePost_createPostReaction(
    $input: CreatePostReactionInput!
  ) {
    createPostReaction(input: $input) {
      data {
        _id
        reactionType
        postV2 {
          _id
          reactionCount
          currentUserReaction {
            _id
            reactionType
          }
          tags {
            _id
          }
          announcement {
            _id
          }
          sponsorData {
            audience {
              group {
                _id
              }
            }
          }
          group {
            _id
            isGlobalSingleton
          }
          organization {
            _id
          }
          event {
            _id
          }
        }
      }
    }
  }
`;

const calledVar = makeVar(false);

export const useAutoLikePost = (options: { validRoutes: string[] }): void => {
  const [createPost] = useMutation<
    useAutoLikePost_createPostReaction,
    useAutoLikePost_createPostReactionVariables
  >(LIKE_POST);
  const { validRoutes } = options;
  const router = useRouter();

  const toast = useToast();

  const isValidRoute = validRoutes.reduce(
    (prev, route) => prev || router.asPath.includes(route),
    false
  );
  React.useEffect(() => {
    const postId = router.query.likePost;
    if (
      !router.isReady ||
      !postId ||
      !isValidRoute ||
      calledVar() ||
      typeof postId !== 'string'
    ) {
      return;
    }

    calledVar(true);

    createPost({
      variables: {
        input: { postId, reactionType: EReactionType.LIKE },
      },
    })
      .then((result) => {
        toast({
          title: `Successfully liked post`,
          status: 'success',
        });
        const post = result.data?.createPostReaction.data?.postV2;
        if (post) {
          post_liked({
            entity: {
              id: post._id,
              type: 'post',
            },
            engagement: {
              reactionType: 'like',
            },
            location: {
              postedIn: post.event ? 'event' : post.organization ? 'organization' : post.group?.isGlobalSingleton ? 'home' : 'group',
              eventId: post.event?._id,
              organizationId: post.organization?._id,
              groupId: !post.organization && !post.event ? post.group?._id : undefined,
            },
          });
        }
      })
      .catch(() =>
        toast({
          title: `Could not like post`,
          status: 'error',
        })
      )
      .finally(() => {
        const { likePost, ...queryWithoutLikePost } = router.query;
        router.replace({ query: queryWithoutLikePost });
      });
  }, [isValidRoute, createPost, toast, router]);
};
